<template>
  <div class="login-container">
    <div class="login-card">
      <div class="logo-container">
        <img src="@/assets/img/Trazalogis.png" alt="Trazalogis Logo" class="logo">
      </div>
      <h1 class="login-title">Ubica tus activos!</h1>
      <p class="login-subtitle">Ingresa tus datos aquí</p>
      
      <div class="form-group">
        <label for="email">Correo electrónico</label>
        <input 
          type="email" 
          id="email" 
          v-model="email" 
          class="form-control" 
          :class="{ 'is-invalid': emailError }"
          placeholder="demo@trazalogis.com"
          @keyup.enter="login"
          @input="validateEmail"
        >
        <div v-if="emailError" class="invalid-feedback">
          {{ emailError }}
        </div>
      </div>
      
      <div class="form-group">
        <label for="password">Contraseña</label>
        <input 
          type="password" 
          id="password" 
          v-model="password" 
          class="form-control" 
          :class="{ 'is-invalid': passwordError }"
          placeholder="••••••••"
          @keyup.enter="login"
        >
        <div v-if="passwordError" class="invalid-feedback">
          {{ passwordError }}
        </div>
      </div>

      <div class="form-group form-check">
        <input 
          type="checkbox" 
          class="form-check-input" 
          id="rememberMe" 
          v-model="rememberMe"
        >
        <label class="form-check-label" for="rememberMe">Recordarme</label>
      </div>
      
      <button 
        @click="login" 
        class="btn btn-primary btn-block login-btn" 
        :disabled="loading || loginAttempts >= 5"
      >
        <span v-if="loading">Cargando...</span>
        <span v-else-if="loginAttempts >= 5">Demasiados intentos. Espere 15 minutos.</span>
        <span v-else>Ingresar</span>
      </button>
      
      <div class="text-center mt-3">
        <a href="#" @click.prevent="showRecoveryModal = true">¿Olvidaste tu contraseña?</a>
      </div>
      
      <div v-if="error" class="alert alert-danger mt-3">
        {{ error }}
      </div>
      
      <div class="footer-text">
        2025 Trazalogis. Todos los derechos reservados.
      </div>
    </div>

    <!-- Modal de Recuperación de Contraseña -->
    <div class="modal" v-if="showRecoveryModal">
      <div class="modal-dialog">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">Recuperar Contraseña</h5>
            <button type="button" class="btn-close" @click="showRecoveryModal = false"></button>
          </div>
          <div class="modal-body">
            <div class="form-group">
              <label for="recoveryEmail">Correo electrónico</label>
              <input 
                type="email" 
                id="recoveryEmail" 
                v-model="recoveryEmail" 
                class="form-control"
                placeholder="Ingresa tu correo electrónico"
              >
            </div>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" @click="showRecoveryModal = false">Cancelar</button>
            <button type="button" class="btn btn-primary" @click="recoverPassword" :disabled="recoveryLoading">
              {{ recoveryLoading ? 'Enviando...' : 'Enviar' }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions } from 'vuex'

export default {
  name: 'Login',
  data() {
    return {
      email: '',
      password: '',
      loading: false,
      error: null,
      emailError: null,
      passwordError: null,
      rememberMe: false,
      loginAttempts: 0,
      lastAttemptTime: null,
      showRecoveryModal: false,
      recoveryEmail: '',
      recoveryLoading: false
    }
  },
  created() {
    // Recuperar email si existe en localStorage
    const savedEmail = localStorage.getItem('rememberedEmail')
    if (savedEmail) {
      this.email = savedEmail
      this.rememberMe = true
    }
    
    // Recuperar intentos de login
    const attempts = localStorage.getItem('loginAttempts')
    const lastAttempt = localStorage.getItem('lastAttemptTime')
    if (attempts && lastAttempt) {
      const timeDiff = Date.now() - parseInt(lastAttempt)
      if (timeDiff < 900000) { // 15 minutos
        this.loginAttempts = parseInt(attempts)
        this.lastAttemptTime = parseInt(lastAttempt)
      } else {
        this.resetLoginAttempts()
      }
    }
  },
  methods: {
    ...mapActions({
      loginAction: 'auth/login',
      requestPasswordReset: 'auth/requestPasswordReset'
    }),
    validateEmail() {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
      this.emailError = !emailRegex.test(this.email) ? 'Ingrese un correo electrónico válido' : null
    },
    resetLoginAttempts() {
      this.loginAttempts = 0
      this.lastAttemptTime = null
      localStorage.removeItem('loginAttempts')
      localStorage.removeItem('lastAttemptTime')
    },
    updateLoginAttempts() {
      this.loginAttempts++
      this.lastAttemptTime = Date.now()
      localStorage.setItem('loginAttempts', this.loginAttempts)
      localStorage.setItem('lastAttemptTime', this.lastAttemptTime)
    },
    async login() {
      this.error = null
      this.passwordError = null
      
      if (this.loginAttempts >= 5) {
        const timeDiff = Date.now() - this.lastAttemptTime
        if (timeDiff < 900000) { // 15 minutos
          this.error = 'Demasiados intentos. Por favor, espere 15 minutos.'
          return
        } else {
          this.resetLoginAttempts()
        }
      }

      if (!this.email || !this.password) {
        this.error = 'Por favor ingrese su correo y contraseña'
        return
      }

      if (this.emailError) {
        return
      }
      
      this.loading = true
      
      try {
        await this.loginAction({ email: this.email, password: this.password })
        
        if (this.rememberMe) {
          localStorage.setItem('rememberedEmail', this.email)
        } else {
          localStorage.removeItem('rememberedEmail')
        }
        
        this.resetLoginAttempts()
        this.$router.push('/inventario')
      } catch (err) {
        this.updateLoginAttempts()
        this.error = err.message || 'Error al iniciar sesión'
      } finally {
        this.loading = false
      }
    },
    async recoverPassword() {
      if (!this.recoveryEmail) {
        this.error = 'Por favor ingrese su correo electrónico'
        return
      }

      this.recoveryLoading = true
      this.error = null

      try {
        await this.requestPasswordReset(this.recoveryEmail)
        this.showRecoveryModal = false
        this.$toast.success('Se ha enviado un correo con las instrucciones para recuperar tu contraseña')
      } catch (err) {
        this.error = err.message || 'Error al solicitar recuperación de contraseña'
      } finally {
        this.recoveryLoading = false
      }
    }
  }
}
</script>

<style scoped>
.login-container {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100vh;
  background-color: #f5f7fa;
  background-image: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
}

.login-card {
  width: 400px;
  background-color: #fff;
  border-radius: 10px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
  padding: 40px;
}

.logo-container {
  text-align: center;
  margin-bottom: 20px;
}

.logo {
  max-width: 180px;
  height: auto;
}

.login-title {
  font-size: 24px;
  font-weight: 600;
  color: #2c3e50;
  margin-bottom: 5px;
  text-align: center;
}

.login-subtitle {
  font-size: 16px;
  color: #7f8c8d;
  margin-bottom: 30px;
  text-align: center;
}

.form-group {
  margin-bottom: 20px;
}

label {
  display: block;
  margin-bottom: 8px;
  font-weight: 500;
  color: #2c3e50;
}

.form-control {
  width: 100%;
  padding: 12px 15px;
  border: 1px solid #ddd;
  border-radius: 5px;
  font-size: 16px;
  transition: border-color 0.3s;
}

.form-control:focus {
  border-color: #3f51b5;
  outline: none;
}

.login-btn {
  width: 100%;
  padding: 12px;
  background-color: #3f51b5;
  color: #fff;
  border: none;
  border-radius: 5px;
  font-size: 16px;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.3s;
}

.login-btn:hover {
  background-color: #303f9f;
}

.login-btn:disabled {
  background-color: #b0bec5;
  cursor: not-allowed;
}

.footer-text {
  margin-top: 30px;
  text-align: center;
  font-size: 14px;
  color: #95a5a6;
}

.modal {
  display: block;
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 1050;
}

.modal-dialog {
  margin: 1.75rem auto;
  max-width: 500px;
}

.modal-content {
  position: relative;
  background-color: #fff;
  border-radius: 10px;
  padding: 20px;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 1px solid #dee2e6;
  padding-bottom: 15px;
  margin-bottom: 15px;
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  border-top: 1px solid #dee2e6;
  padding-top: 15px;
  margin-top: 15px;
}

.form-check {
  margin-bottom: 20px;
}

.invalid-feedback {
  display: block;
  color: #dc3545;
  font-size: 0.875em;
  margin-top: 5px;
}
</style>
